/**
 * Truth resource (CT-5719): the confirmed facts of a record. CT-5717 landed its
 * summary first, for the line shared by the three Knowledge subtabs; CT-5719
 * adds the facts, one fact's detail, Request change and Report an issue.
 *
 * The paths are ASSUMED, `TODO(CT-5719 / backend)`. The chips, filters, search
 * and order narrow on the server.
 */

import { validateApiResponse } from '../shared/utils/apiResponse.js';
import { request } from './mockTransport.js';

export const mapApiTruthSummaryToUi = (raw = {}) => ({
  factsCount: raw.facts_count ?? raw.factsCount ?? 0,
  dueToReconfirm: raw.due_to_reconfirm ?? raw.dueToReconfirm ?? 0,
  canonSigned: raw.canon_signed ?? raw.canonSigned ?? 0,
});

/** TODO(CT-5719 / backend): `POST /v0/profile/truth/summary` with `{ entity_type, entity_id }`. */
export const getTruthSummary = async ({ entityType, entityId } = {}) => {
  const response = await request('/v0/profile/truth/summary', { entity_type: entityType, entity_id: entityId });
  return validateApiResponse(response, 'Failed to load the Truth summary');
};

export { __resetMockTruthForTests } from './__mocks__/truth.mock.js';

const record = (entityType, entityId) => ({ entity_type: entityType, entity_id: entityId });

/**
 * One fact of the Truth plane. `type` is `deal_commitment` or `other_fact`;
 * `status` is `confirmed` or `reconfirm_due`; `source_kind` is the Sandbox's
 * Said / Documented / Observed.
 */
export const mapApiTruthFactToUi = (raw = {}) => ({
  id: raw.id ?? raw.fact_id ?? '',
  type: raw.type ?? null,
  label: raw.label ?? '',
  value: raw.value ?? '',
  sourceKind: raw.source_kind ?? raw.sourceKind ?? null,
  confirmedSource: raw.confirmed_source ?? raw.confirmedSource ?? null,
  sourceChannel: raw.source_channel ?? raw.sourceChannel ?? null,
  confirmedAt: raw.confirmed_at ?? raw.confirmedAt ?? null,
  status: raw.status ?? 'confirmed',
  confirmedByMe: Boolean(raw.confirmed_by_me ?? raw.confirmedByMe ?? false),
  canRequestChange: Boolean(raw.can_request_change ?? raw.canRequestChange ?? false),
  quote: raw.quote ?? null,
  since: raw.since ?? raw.confirmed_at ?? null,
  expiresAt: raw.expires_at ?? raw.expiresAt ?? null,
  usedIn: raw.used_in ?? raw.usedIn ?? [],
  visibility: raw.visibility ?? null,
  history: (raw.history ?? []).map((entry) => ({ at: entry.at ?? null, text: entry.text ?? '' })),
  reportOpen: Boolean(raw.report_open ?? raw.reportOpen ?? false),
});

/** A section of Truth: Deal commitments, Other facts or Canon. */
export const mapApiTruthSectionToUi = (raw = {}) => ({
  key: raw.key ?? '',
  facts: (raw.facts ?? []).map(mapApiTruthFactToUi),
});

/**
 * TODO(CT-5719 / backend): `POST /v0/profile/truth/list` with `{ entity_type,
 * entity_id, quick_filter, filters: { status, source_kind }, search: { query, fields },
 * order_by, order_direction }`.
 * Answers `{ sections: [{ key, facts }] }`, the three sections always present.
 */
export const listTruthFacts = async ({ entityType, entityId, quickFilter = 'all', filters, search, orderBy, orderDirection } = {}) => {
  const response = await request('/v0/profile/truth/list', {
    ...record(entityType, entityId),
    quick_filter: quickFilter,
    ...(filters ? { filters } : {}),
    ...(search ? { search } : {}),
    ...(orderBy ? { order_by: orderBy } : {}),
    ...(orderDirection ? { order_direction: orderDirection } : {}),
  });
  return validateApiResponse(response, 'Failed to load Truth');
};

/** TODO(CT-5719 / backend): `POST /v0/profile/truth/facts/get` with `{ entity_type, entity_id, fact_id }`. */
export const getTruthFact = async ({ entityType, entityId, factId } = {}) => {
  const response = await request('/v0/profile/truth/facts/get', { ...record(entityType, entityId), fact_id: factId });
  return validateApiResponse(response, 'Failed to load the fact');
};

/**
 * TODO(CT-5719 / backend): `POST /v0/profile/truth/facts/request-change` with
 * `{ entity_type, entity_id, fact_id }` — opens a change request in Governance Studio.
 */
export const requestFactChange = async ({ entityType, entityId, factId } = {}) => {
  const response = await request('/v0/profile/truth/facts/request-change', { ...record(entityType, entityId), fact_id: factId });
  return validateApiResponse(response, 'Failed to open the change request');
};

/** TODO(CT-5719 / backend): `POST /v0/profile/truth/facts/report` with `{ entity_type, entity_id, fact_id }`. */
export const reportTruthFactIssue = async ({ entityType, entityId, factId } = {}) => {
  const response = await request('/v0/profile/truth/facts/report', { ...record(entityType, entityId), fact_id: factId });
  return validateApiResponse(response, 'Failed to report the issue');
};
